import type { ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import { Building2, ChevronRight } from "lucide-react";
import { usePatientConnectionsQuery } from "@/frontend/queries/dashboard/patients";
import { formatDateTime, logoUrl } from "@/frontend/utils/shared";
import { cn } from "@/frontend/lib/utils";
import { isConnectionHealthy } from "@/frontend/utils/dashboard/connections";
import {
  CONNECTION_CHANNEL_LABEL,
  CONNECTION_CHANNEL_TONE,
  CONNECTION_STATUS_LABEL,
} from "@/frontend/types/dashboard/connections";

/**
 * The health systems this patient has connected, one row per source with its
 * channel, health and share of the stored records.
 *
 * @param patientId the `patient_org_membership.id` whose connections to list
 * @param whenEmpty rendered instead of the list when nothing is connected
 */
export function ConnectedHealthSystems({
  patientId,
  whenEmpty,
}: {
  patientId: string;
  whenEmpty?: ReactNode;
}) {
  const { data: connectionList, isPending } = usePatientConnectionsQuery(patientId);

  const connections = connectionList?.connections ?? [];
  const recordTotal = connectionList?.recordTotal ?? 0;

  if (isPending) return null;
  if (connections.length === 0) return whenEmpty ?? null;

  return (
    <section className="overflow-hidden rounded-lg border bg-card">
      <div className="flex h-10 items-center justify-between gap-2 border-b px-4">
        <h2 className="text-meta font-semibold uppercase tracking-wide text-foreground">
          Connected health systems
        </h2>
        <span className="text-meta tabular-nums text-muted-foreground">
          {connections.length} · {recordTotal.toLocaleString()} records
        </span>
      </div>

      <div className="divide-y">
        {connections.map((connection) => {
          const share = recordTotal > 0 ? Math.round((connection.recordCount / recordTotal) * 100) : 0;
          const url = logoUrl(connection.sourceLogo);
          const healthy = isConnectionHealthy(connection.status);

          return (
            <Link
              key={connection.id}
              to="/dashboard/patients/$patientId/connections/$connectionId"
              params={{ patientId, connectionId: connection.id }}
              className="flex items-center gap-3 px-4 py-3 transition-colors hover:bg-accent/40"
            >
              <span className="flex size-8 shrink-0 items-center justify-center overflow-hidden rounded-md border bg-background">
                {url ? (
                  <img
                    src={url}
                    alt=""
                    className="size-5 object-contain"
                    data-ph-no-capture
                    onError={(event) => {
                      event.currentTarget.style.display = "none";
                    }}
                  />
                ) : (
                  <Building2 className="size-4 text-muted-foreground" />
                )}
              </span>

              <span className="min-w-0 flex-1">
                {/* Two lines, not an ellipsis: systems sharing a long prefix
                    ellipsize to the same string and stop being distinguishable. */}
                <span className="line-clamp-2 text-body font-medium" data-patient>
                  {connection.sourceName}
                </span>
                <span className="mt-0.5 flex items-center gap-1.5 text-meta text-muted-foreground">
                  <span
                    className={cn(
                      "size-1.5 shrink-0 rounded-full",
                      healthy ? "bg-emerald-500" : "bg-amber-500",
                    )}
                  />
                  {CONNECTION_STATUS_LABEL[connection.status] ?? connection.status}
                  {connection.lastPullAt
                    ? ` · last pull ${formatDateTime(connection.lastPullAt)}`
                    : " · no pull yet"}
                </span>
              </span>

              {/* Channel and counts share the row's centre line, so the badge
                  cannot float above the numbers beside it. */}
              <span
                className={cn(
                  "shrink-0 rounded-full px-2 py-0.5 text-meta font-medium",
                  CONNECTION_CHANNEL_TONE[connection.channel],
                )}
              >
                {CONNECTION_CHANNEL_LABEL[connection.channel]}
              </span>

              <span className="shrink-0 whitespace-nowrap text-right text-meta tabular-nums text-muted-foreground">
                {connection.recordCount.toLocaleString()} records · {share}%
              </span>

              <ChevronRight className="size-4 shrink-0 text-muted-foreground" />
            </Link>
          );
        })}
      </div>
    </section>
  );
}
